import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';
import FileSaver from 'file-saver';
import api from 'src/app/api';
import { buildEncodedQueryString, CSVDataType, DateTimeFormat, downloadCsv, getLocalDateTime } from 'src/common';
import { PagedResult } from 'src/store';
import { REPORT_STATUS_NAMES } from '../../constants';
import {
  ProcessScheduledInstance as ApproveScheduledInstance,
  BatchProgress,
  BulkBatchReportRequest,
  BulkBatchReportRequestFiltered,
  BulkReport,
  BulkReportLogList,
  BulkReportRecord,
  BulkReportRecordEmailPreview,
  DownloadBatchLogsCsvRequest,
  DownloadBatchRecordsCsvRequest,
  DownloadBatchReports,
  GroupReport,
  InitiateBulkRequest,
  ReviewScheduledInstanceRecord,
} from './types';

export enum ReportingBatchActionTypes {
  InitiateBulkRequest = '@@reporting/batch/InitiateBulkRequest',
  FetchBulkReport = '@@reporting/batch/FetchBulkReport',
  FetchBulkReportLogList = '@@reporting/batch/FetchBulkReportLogList',
  FetchBulkReportRecordList = '@@reporting/batch/FetchBulkReportRecordList',
  FetchBulkProgress = '@@reporting/batch/FetchBulkProgress',
  DownloadBulkReports = '@@reporting/DownloadBulkReports',
  DownloadBatchRecordsCsv = '@@reporting/DownloadBatchRecordsCsv',
  DownloadBatchLogsCsv = '@@reporting/DownloadBatchLogsCsv',
  TerminateBulkRequest = '@@reporting/TerminateBulkRequest',
  FetchGroupReport = '@@reporting/FetchGroupReport',
  ReviewScheduledInstanceRecord = '@@reporting/UpdateClientStatus',
  ApproveScheduledInstance = '@@reporting/ApproveScheduledInstance',
  FetchEmailPreview = '@@reporting/batch/PreviewBulkRecordEmail',
}

export enum ReportingBatchApi {
  InitiateBulkRequest = '/reporting/batch/InitiateBulkRequest',
  FetchBulkReport = '/reporting/batch/GetBulkReport',
  FetchBulkReportLogList = '/reporting/batch/ListBulkReportLog',
  FetchBulkReportRecordList = '/reporting/batch/ListBulkReportRecord',
  FetchBulkProgress = '/reporting/batch/GetBulkProgress',
  DownloadBulkReports = '/reporting/batch/DownloadBatchReports',
  TerminateBulkRequest = '/reporting/batch/TerminateBulkRequest',
  FetchGroupReport = '/reporting/batch/GetGroupReport',
  ReviewScheduledInstanceRecord = '/reporting/scheduledevent/ReviewScheduledInstanceRecord',
  ApproveScheduledInstance = '/reporting/scheduledevent/ApproveScheduledInstance',
  FetchEmailPreview = '/reporting/batch/PreviewBulkRecordEmail',
}

export const downloadBulkReports = createAsyncThunk(ReportingBatchActionTypes.DownloadBulkReports, async (request: DownloadBatchReports) => {
  const options: AxiosRequestConfig = {
    responseType: 'blob',
  };

  const response = await api.post<BlobPart>(`${ReportingBatchApi.DownloadBulkReports}`, request, options);

  const contentType = response.headers['content-type'];
  const fileName = `Reports.zip`;
  const data = new Blob([response.data], { type: contentType });
  FileSaver.saveAs(data, fileName, { autoBom: false });
  return 'success';
});

export const downloadBatchRecordsCsv = createAsyncThunk(
  ReportingBatchActionTypes.DownloadBatchRecordsCsv,
  async (request: DownloadBatchRecordsCsvRequest) => {
    try {
      const response = await api.post<PagedResult<BulkReportRecord>>(ReportingBatchApi.FetchBulkReportRecordList, {
        batchId: request.batchId,
        pageSize: 999999,
        page: 1,
        queryFields: [],
      });

      const data =
        request.selectedRows.length === 0
          ? response.data.results
          : response.data.results.filter((record) => request.selectedRows.includes(record.recordIndex));
      const csvData: CSVDataType[] = data.map((record) => ({
        ID: record.clientOrGroupId,
        Name: record.clientName,
        AFSL: record.afslName,
        Practice: record.practiceName,
        Adviser: record.adviserName,
        Filename: record.reportFileName,
        Status: REPORT_STATUS_NAMES[record.status] ?? record.status,
        'Completed At': record.completedOn ? getLocalDateTime(record.completedOn, DateTimeFormat.DateTimeShort) : '',
        'Sent At': record.scheduledInstanceRecord?.emailSentOn ?? '',
        'Uploaded At': record.scheduledInstanceRecord?.documentUploadedOn ?? '',
        'Created On': record.createdOn ? getLocalDateTime(record.createdOn, DateTimeFormat.DateTimeShort) : '',
      }));
      downloadCsv(csvData, `BatchRecords`);
      return { message: 'CSV downloaded' };
    } catch (error) {
      return Promise.reject('Error downloading CSV');
    }
  }
);

export const downloadBatchLogsCsv = createAsyncThunk(
  ReportingBatchActionTypes.DownloadBatchLogsCsv,
  async (request: DownloadBatchLogsCsvRequest) => {
    try {
      const queryString = buildEncodedQueryString({
        batchId: request.batchId,
        page: 0,
        pageSize: 999999,
      });
      const response = await api.get<BulkReportLogList>(`${ReportingBatchApi.FetchBulkReportLogList}${queryString}`);

      const data =
        request.selectedLogIds.length === 0
          ? response.data.results
          : response.data.results.filter((log) => request.selectedLogIds.includes(log.id));
      const csvData: CSVDataType[] = data.map((log) => ({
        'Related User ID': log.relatedUserId,
        Description: log.description ?? '',
        'Client ID': log.clientId ?? '',
        'Client Name': log.clientName ?? '',
        'Created On': log.createdOn ? getLocalDateTime(log.createdOn, DateTimeFormat.DateTimeShort) : '',
      }));
      downloadCsv(csvData, 'Logs');
      return { message: 'CSV downloaded' };
    } catch (error) {
      return Promise.reject('Error downloading CSV');
    }
  }
);

export const terminateBulkRequest = createAsyncThunk(ReportingBatchActionTypes.TerminateBulkRequest, async (request: InitiateBulkRequest) => {
  const response = await api.post(`${ReportingBatchApi.TerminateBulkRequest}`, request);
  return Promise.resolve<BulkReport>(response.data);
});

export const initiateBulkRequest = createAsyncThunk(ReportingBatchActionTypes.InitiateBulkRequest, async (request: InitiateBulkRequest) => {
  const response = await api.post(`${ReportingBatchApi.InitiateBulkRequest}`, request);
  return Promise.resolve<BulkReport>(response.data);
});

export const fetchBulkReport = createAsyncThunk(ReportingBatchActionTypes.FetchBulkReport, async (batchId: string) => {
  const queryString = buildEncodedQueryString({
    batchId: batchId,
  });
  const response = await api.get<BulkReport>(`${ReportingBatchApi.FetchBulkReport}${queryString}`);
  return response.data;
});

export const fetchBulkProgress = createAsyncThunk(ReportingBatchActionTypes.FetchBulkProgress, async (batchId: string) => {
  const queryString = buildEncodedQueryString({
    batchId: batchId,
  });
  const response = await api.get<BatchProgress>(`${ReportingBatchApi.FetchBulkProgress}${queryString}`);
  return response.data;
});

export const fetchGroupReport = createAsyncThunk(ReportingBatchActionTypes.FetchGroupReport, async (batchId: string) => {
  const queryString = buildEncodedQueryString({
    batchId: batchId,
  });
  const response = await api.get<GroupReport>(`${ReportingBatchApi.FetchGroupReport}${queryString}`);
  return response.data;
});

export const fetchBulkReportRecordList = createAsyncThunk(
  ReportingBatchActionTypes.FetchBulkReportRecordList,
  async (request: BulkBatchReportRequestFiltered) => {
    const response = await api.post<PagedResult<BulkReportRecord>>(ReportingBatchApi.FetchBulkReportRecordList, request);
    return response.data;
  }
);

export const fetchBulkReportLogList = createAsyncThunk(ReportingBatchActionTypes.FetchBulkReportLogList, async (request: BulkBatchReportRequest) => {
  const queryString = buildEncodedQueryString({
    batchId: request.batchId,
    page: request.page,
    pageSize: request.pageSize,
  });
  const response = await api.get<BulkReportLogList>(`${ReportingBatchApi.FetchBulkReportLogList}${queryString}`);
  return response.data;
});

export const reviewScheduledInstanceRecord = createAsyncThunk(
  ReportingBatchActionTypes.ReviewScheduledInstanceRecord,
  async (request: ReviewScheduledInstanceRecord) => {
    await api.put(ReportingBatchApi.ReviewScheduledInstanceRecord, request);
  }
);

export const approveScheduledInstance = createAsyncThunk(
  ReportingBatchActionTypes.ApproveScheduledInstance,
  async (request: ApproveScheduledInstance) => {
    await api.post(ReportingBatchApi.ApproveScheduledInstance, request);
  }
);

export const fetchEmailPreview = createAsyncThunk(ReportingBatchActionTypes.FetchEmailPreview, async (request: { batchId: string; recordIndex: number }) => {
  const queryString = buildEncodedQueryString({
    batchId: request.batchId,
    recordIndex: request.recordIndex,
  });
  const response = await api.get<BulkReportRecordEmailPreview>(`${ReportingBatchApi.FetchEmailPreview}${queryString}`);
  return response.data;
});
